import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, FormGroup} from "@angular/forms";
import {DomSanitizer} from "@angular/platform-browser";
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {environment} from "../../../environments/environment";
import {MediaItemWithComments} from "../lightbox/lightbox.component";
import {DirectUploadService} from "../../_services/direct-upload.service";

export interface MediaItemWithManipulation extends MediaItemWithComments {
    cropping?: boolean,
}

@Component({
    selector: 'app-edit-item',
    templateUrl: './edit-item.component.html',
    styleUrl: './edit-item.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditItemComponent {
    currentMedia: MediaItemWithManipulation | undefined;
    editItemForm: FormGroup;
    cropping: boolean = false;
    rotation: number = 0;
    loading: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) private data: MediaItemWithComments,
                protected dialogRef: MatDialogRef<EditItemComponent>,
                public dialog: MatDialog, private formBuilder: FormBuilder,
                private sanitizer: DomSanitizer,
                private uploader: DirectUploadService,
                private contentService: ContentService,
                private snackbarService: SnackbarService) {

        this.editItemForm = this.formBuilder.group({
            title: [''],
            description: [''],
            coverImage: [''],
        });

        if (data) {
            this.currentMedia = data;
            this.editItemForm.patchValue({
                title: this.currentMedia.title,
                description: this.currentMedia.description,
                coverImage: this.currentMedia.cover_image
            });
        }
    }

    initiateCrop(): void {
        if (!this.currentMedia) return;
        this.contentService.getMedia(this.currentMedia.id).subscribe({
            next: r => {
                this.currentMedia = r as MediaItemWithManipulation;
                this.contentService.toDataURLBase64(`${environment.apiUrl}v2/api/media/` + this.currentMedia.id + '/download/').then((r): void => {
                    this.currentMedia!.image = r;
                    this.currentMedia!.cropping = true;
                    this.cropping = true;
                });
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
        var myDiv: HTMLElement | null = document.getElementById('content');
        if (myDiv) {
            myDiv.scrollTop = 0;
        }
    }

    imageCropped(file: any, event: any): void {
        const originalName = file.originalFile?.name || 'cropped-image.png';
        const originalType = file.originalFile?.type || event.blob?.type || 'image/png';

        file.croppedFile = new File(
            [event.blob],
            originalName,
            {
                type: originalType,
                lastModified: Date.now()
            }
        );
        file.tempFile = this.sanitizer.bypassSecurityTrustUrl(event.objectUrl);
    }

    async saveCrop(file: any): Promise<void> {
        if (!this.currentMedia || this.loading) return;
        this.loading = true;

        try {
            if (!file.croppedFile) {
                throw new Error('No cropped file found');
            }

            const croppedFile: File = file.croppedFile instanceof File
                ? file.croppedFile
                : new File(
                    [file.croppedFile],
                    'cropped-image.png',
                    {
                        type: file.croppedFile.type || 'image/png',
                        lastModified: Date.now()
                    }
                );

            const updatedMedia = await this.uploader.replaceMediaFile(croppedFile, this.currentMedia.id, 'image', true);
            if (!updatedMedia) {
                throw new Error('Media update failed');
            }

            this.snackbarService.openSnackBar('Media Successfully Updated', 'success');
            file.image = file.tempFile;
            this.rotation = 0;
            file.cropping = false;
            this.cropping = false;
        } catch (error: unknown) {
            const message: string = error instanceof Error ? error.message : String(error);

            this.snackbarService.openSnackBar(`${message}`, 'error');
            console.error(error);
        } finally {
            this.loading = false;
        }
    }

    cancelCrop(file: any): void {
        this.rotation = 0;
        file.tempFile = null;
        file.cropping = false;
        this.cropping = false;
    }

    rotateCanvas(): void {
        this.rotation++
    }

    saveMedia(): void {
        if (!this.currentMedia) return;
        let body = {
            title: this.editItemForm.value.title,
            description: this.editItemForm.value.description,
            cover_image: this.editItemForm.value.coverImage,
        }
        this.contentService.patchMedia(this.currentMedia.id, body).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Media Successfully Updated', 'success')
                this.dialogRef.close();
                location.reload();
            }, error: error => {
                this.snackbarService.openSnackBar(body.title + ':' + error, 'error')
                console.error(error)
            }
        });
    }

    checkIfMemory(): boolean {
        // Only show if accessing lightbox from memory
        return location.href.includes('memory');
    }
}
